import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createTheme,
  ThemeProvider,
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Divider,
} from '@mui/material';
import { loginUser, clearError } from '../../store/slices/authSlice';
import { RootState } from '../../store';
import { AppDispatch } from '../../store';
import logo from '../../assets/images/cacxi-white-logo.svg';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Notifications from '../common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';
import GoogleSignInButton from './GoogleSignInButton';

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error } = useSelector((state: RootState) => state.auth);
  //Clear error
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  // Check for success param when component mounts
  useEffect(() => {
    const success = searchParams.get('success');
    if (success === 'true') {
      setNotification({
        open: true,
        message: 'Please check your email for further instructions to complete your account setup.',
        severity: SEVERITY.SUCCESS,
      });
      // Clear the success param from URL
      navigate('/', { replace: true });
    }
  }, [navigate, searchParams]);

  // Light theme specifically for login
  const lightTheme = createTheme({
    palette: {
      mode: 'dark',
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(loginUser({ email, password }));
  };

  return (
    <Box className="h-screen flex items-center justify-center font-sans login-bg">
      <div className="w-full max-w-7xl flex flex-col lg:flex-row  h-screen">
        <div className="hidden lg:flex w-full lg:w-1/2  flex-col justify-center items-center">
          <div className="text-white lg:p-16 flex flex-col justify-center items-center lg:items-start z-10 w-ful max-w-xl">
            <img className="text-white w-36 mb-2" src={logo} alt="CAC_logo" />
            <div className="hidden lg:flex flex-wrap">
              <Typography className="text-3xl lg:text-4xl font-bold leading-[3rem]">
                Smarter Solutions,
              </Typography>
              <Typography className="text-3xl lg:text-4xl font-bold leading-[3rem]">
                Seamless Growth
              </Typography>
            </div>
            <Typography className="hidden lg:block text-sm lg:text-lg font-normal mt-2">
              Revolutionize Bookings and boost Marketing with our AI powered smart tool
            </Typography>
            {/* <Box
              sx={{
                width: '100%',
                maxWidth: 800,
                mt: 4,
                display: { xs: 'none', lg: 'block' }, // hide on small screens
              }}
            >
              <video
                width="100%"
                height="auto"
                muted
                loop
                controls
                // poster="/images/demo-poster.jpg"
                style={{ borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,.35)' }}
              >
                <source
                  src="https://s3.ap-southeast-2.amazonaws.com/cacxi.com.au/videos/Cacxi+Product+Demo.mp4"
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </Box> */}
          </div>
        </div>
        <Paper
          className="lg:grow h-full xs:p-4 sm:p-8 lg:p-10 xl:p-6 rounded-md bg-transparent flex flex-col justify-center items-center"
          elevation={0}
        >
          <div className="max-w-sm w-full">
            <ThemeProvider theme={lightTheme}>
              <Box
                sx={{
                  backgroundColor: '#21172B',
                  border: '1px solid #291c35',
                  borderRadius: '12px',
                  px: 5,
                  py: 4,
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <img
                    className="block lg:hidden text-white w-28 mb-6 mx-auto"
                    src={logo}
                    alt="CAC_logo"
                  />
                  <Typography
                    sx={{
                      color: 'text.primary',
                      fontSize: '1.75rem',
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Welcome back
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                    Sign in to access your dashboard
                  </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                      },
                    }}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      onClick={() => navigate('/forget-password')}
                      sx={{
                        fontSize: '0.813rem',
                        color: '#a855f7',
                        textTransform: 'none',
                        padding: '4px 8px',
                        minWidth: 'auto',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: '#c084fc',
                        },
                      }}
                    >
                      Forgot Password?
                    </Button>
                  </Box>

                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mt: 2,
                        borderRadius: '12px',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        color: '#fca5a5',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    type="submit"
                    fullWidth
                    disabled={isLoading}
                    className="mt-4 bg-purple-800 py-2 text-md text-white font-bold hover:bg-purple-900"
                    sx={{
                      textTransform: 'none',
                      borderRadius: '12px',
                      py: 1.5,
                    }}
                  >
                    {isLoading ? 'Signing in...' : 'Sign In'}
                  </Button>

                  {/* Divider */}
                  <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
                    <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Typography
                      sx={{
                        px: 2,
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                      }}
                    >
                      or
                    </Typography>
                    <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                  </Box>

                  {/* Google Sign-In */}
                  <GoogleSignInButton mode="signin" disabled={isLoading} />
                </Box>

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Button
                    onClick={() => navigate('/signup')}
                    sx={{
                      fontSize: '0.875rem',
                      color: '#a855f7',
                      textTransform: 'none',
                      padding: 0,
                      minWidth: 'auto',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: '#c084fc',
                      },
                    }}
                  >
                    Non Google Email Sign Up
                  </Button>
                </Box>
              </Box>
            </ThemeProvider>
          </div>
        </Paper>
      </div>
      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default LoginForm;
